import { Button, Combobox } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { getApiError } from '../../lib/api.js';
import type { PrepaidBalance } from '@hue/contracts';
import { prepaidApi } from './api.js';
export function AppointmentBalanceChoice({
  clientId,
  serviceId,
  value,
  onChange,
  date,
  onPurchase,
  purchasedBalance,
}: {
  date?: string | undefined;
  onPurchase?: (() => void) | undefined;
  purchasedBalance?: PrepaidBalance | undefined;
  clientId: string;
  serviceId: string;
  value: string;
  onChange: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [remembered, setRemembered] = useState<PrepaidBalance>();
  const query = useQuery({
    queryKey: ['company', 'prepaid', 'choice', clientId, serviceId, search],
    queryFn: () =>
      prepaidApi.list(clientId, { search, referenceId: serviceId, limit: 25, offset: 0 }),
    enabled: !!clientId && !!serviceId,
    retry: false,
  });
  const eligible = (b: PrepaidBalance) =>
    b.kind === 'SERVICE' &&
    b.status === 'AVAILABLE' &&
    b.available > 0 &&
    (!date || ((!b.expiresOn || date <= b.expiresOn) && (!b.releasedOn || date >= b.releasedOn)));
  const rows = [...(query.data?.balances ?? [])];
  if (purchasedBalance && !rows.some((b) => b.id === purchasedBalance.id))
    rows.unshift(purchasedBalance);
  const balances = rows.filter(eligible);
  // Sem busca e sem escolha, lista vazia quer dizer que o cliente não tem sessão para o serviço.
  const noBalances = query.isSuccess && !search && !value && balances.length === 0;
  const selected =
    rows.find((b) => b.id === value) ?? (remembered?.id === value ? remembered : undefined);

  return (
    <div className="grid gap-2">
      <Combobox
        label="Usar pacote já comprado (opcional)"
        placeholder="Buscar nos pacotes deste cliente"
        selectedLabel={selected ? `${selected.packageName} · venda ${selected.saleNumber}` : ''}
        onSelect={(option) => {
          const id = (option as { value: string }).value;
          setRemembered(balances.find((b) => b.id === id));
          onChange(id);
        }}
        onClear={() => onChange('')}
        onQueryChange={setSearch}
        disabled={!clientId || !serviceId || noBalances}
        loading={query.isFetching}
        error={
          query.error
            ? getApiError(query.error)
            : selected && !eligible(selected)
              ? 'A sessão selecionada não está disponível nesta data. Escolha outro saldo ou altere a data.'
              : undefined
        }
        hint={
          noBalances
            ? 'Este cliente não tem sessão livre deste serviço na data.'
            : 'Agendar reserva uma sessão. O desconto ocorre ao concluir ou conforme a regra de falta e cancelamento.'
        }
        options={balances.map((b) => ({
          value: b.id,
          label: `${b.packageName} · venda ${b.saleNumber}`,
          description: `${b.available} livres · ${b.businessUnitName}`,
        }))}
        emptyLabel={
          !clientId || !serviceId
            ? 'Escolha o cliente e o serviço primeiro'
            : 'Nenhuma sessão livre para este serviço e data'
        }
        status={
          query.data && query.data.total > 25
            ? 'Digite para refinar os primeiros 25 saldos'
            : undefined
        }
      />
      {onPurchase ? (
        <Button
          type="button"
          variant="outline"
          // O rótulo longo quebra em telas estreitas em vez de alargar o diálogo.
          className="h-auto min-h-11 max-w-full whitespace-normal py-2 text-center"
          disabled={!clientId || !serviceId}
          onClick={onPurchase}
        >
          Comprar novo pacote e usar neste agendamento
        </Button>
      ) : null}
    </div>
  );
}
